'use client';
// VX-3 D4 — 상세 히어로 「낮 · 노을 · 밤」 시간대 전환 + 히어로 그림 탭 → 전체 화면 갤러리.
//
// ── SSR HTML 은 한 글자도 바꾸지 않는다 ──
//   이 컴포넌트는 서버에서 null 이다. 히어로 <img>(S1 master JPEG · eager · fetchpriority=high)는 공용 KdHeroMedia 가
//   낸 그대로 남는다 — 네이버·구글이 읽는 것도, LCP 요소도 그 한 장이다. 수화 뒤에만 «같은 상자 안» 에 겹침 층을 얹는다.
//   · 다른 컷은 그 상자(.kd-mf-box · position:relative · 비율 고정) 안 absolute 층이다 → 밀림(CLS) 0.
//   · 층은 «다 받은 뒤» 에만 보인다(onLoad → opacity). 받는 동안 화면은 종전 그림 그대로.
//   · 기존 <img> 의 src·alt 는 건드리지 않는다. 겹친 동안에만 aria-hidden 을 붙이고, 그림 설명(figcaption)은 보이는 컷의 문장으로 바꾼다.
//
// ── 공용 컴포넌트를 고치지 않는다 ──
//   KdHeroMedia 는 상자 안에 자식을 받는 자리가 없다(공용 · 다른 레인 소유). 그래서 수화 뒤 그 상자를 찾아 portal 로 얹는다.
//   상자를 못 찾으면(마크업이 바뀌면) 아무것도 하지 않는다 — 히어로는 종전 그대로다.
//
// ── 자동 전환(방문 시각) ──
//   HERO_TIME_AUTO_SWITCH(lib/apt/detail-vx3.ts)가 true 일 때만: 수화 뒤 idle 에 «한 번», 방문자가 아직 손대지 않았을 때.
//   VX-3 레인 C 보수 판정으로 현재 false 다(대장 ledger_VX3-C.md).
//
// 토글: role="radiogroup" + radio 3개(가진 컷만) · ←→ · 터치 44px · 반투명 남색 칩. 스타일은 rd2-detail.css `.kd-hts*`.
// 계측: hero_time_toggle(props: shot) — 방문자가 컷을 바꿀 때마다, 세션당 3회 상한.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { trackFeature } from '@/lib/analytics';
import { HERO_SHOT_DAY, HERO_TIME_AUTO_SWITCH, heroShotForHour, radioKeyIndex, type HeroTimeOption } from '@/lib/apt/detail-vx3';
import { requestShotsOpen, takeHeroTimeTrackSlot } from './shot-events';

const BOX_SELECTOR = '.kd-hero-vx [data-kd="hero-media"] .kd-mf-box';

export default function HeroShotStage({ options, siteSlug }: { options: HeroTimeOption[]; siteSlug?: string | null }) {
  const [box, setBox] = useState<HTMLElement | null>(null);
  const [active, setActive] = useState<string>(HERO_SHOT_DAY); // 방문자가 고른 컷(라디오 표시)
  const [shown, setShown] = useState<string>(HERO_SHOT_DAY);   // 실제로 보이는 컷(다 받은 뒤에만 따라간다)
  const [mounted, setMounted] = useState<readonly string[]>([]);
  const [loaded, setLoaded] = useState<readonly string[]>([]);
  const touched = useRef(false);
  const shownRef = useRef<string>(HERO_SHOT_DAY);
  const activeRef = useRef<string>(HERO_SHOT_DAY);
  const radios = useRef<(HTMLButtonElement | null)[]>([]);
  const original = useRef<{ cap: Text | null; capText: string; base: HTMLImageElement | null }>({ cap: null, capText: '', base: null });

  // 수화 뒤: 히어로 그림 상자를 찾고, 탭하면 전체 화면 갤러리를 연다(토글을 누른 것은 제외).
  useEffect(() => {
    let el: HTMLElement | null = null;
    try { el = document.querySelector<HTMLElement>(BOX_SELECTOR); } catch { el = null; }
    if (!el) return;
    const fig = el.closest('.kd-mf');
    const capEl = fig ? fig.querySelector('.kd-mf-cap') : null;
    const capNode = capEl && capEl.firstChild && capEl.firstChild.nodeType === 3 ? (capEl.firstChild as Text) : null;
    original.current = {
      cap: capNode,
      capText: capNode ? capNode.nodeValue ?? '' : '',
      base: el.querySelector<HTMLImageElement>('picture > img[data-kd-shot]') ?? el.querySelector<HTMLImageElement>('picture > img'),
    };
    const onClick = (e: MouseEvent) => {
      const t = e.target as Element | null;
      if (t && typeof t.closest === 'function' && t.closest('[data-vx-time]')) return;
      requestShotsOpen({ shot: shownRef.current, via: 'hero' });
    };
    el.addEventListener('click', onClick);
    el.setAttribute('data-vx-zoom', '1');
    setBox(el);
    const saved = original.current;
    return () => {
      el?.removeEventListener('click', onClick);
      el?.removeAttribute('data-vx-zoom');
      if (saved.cap) saved.cap.nodeValue = saved.capText;
      saved.base?.removeAttribute('aria-hidden');
    };
  }, []);

  const markLoaded = useCallback((shot: string) => {
    setLoaded((prev) => (prev.includes(shot) ? prev : [...prev, shot]));
  }, []);

  const choose = useCallback((shot: string, via: 'toggle' | 'key' | 'auto') => {
    if (via !== 'auto') touched.current = true;
    setMounted((prev) => (shot === HERO_SHOT_DAY || prev.includes(shot) ? prev : [...prev, shot]));
    if (activeRef.current !== shot && via !== 'auto' && takeHeroTimeTrackSlot()) {
      try { trackFeature('hero_time_toggle', { shot, via, site_slug: siteSlug ?? null }); } catch { /* 계측 실패는 화면을 막지 않는다 */ }
    }
    activeRef.current = shot;
    setActive(shot);
  }, [siteSlug]);

  // 고른 컷이 «준비되면» 보이는 컷을 바꾼다(낮은 SSR 그림이라 즉시).
  useEffect(() => {
    if (active === HERO_SHOT_DAY || loaded.includes(active)) setShown(active);
  }, [active, loaded]);

  // 보이는 컷에 맞춰 그림 설명·읽기 도구 표식을 맞춘다(조용히 — 읽기 도구에 따로 알리지 않는다).
  useEffect(() => {
    shownRef.current = shown;
    const o = original.current;
    const opt = options.find((x) => x.shot === shown);
    if (o.cap) o.cap.nodeValue = shown === HERO_SHOT_DAY ? o.capText : (opt?.caption || o.capText);
    if (o.base) {
      if (shown === HERO_SHOT_DAY) o.base.removeAttribute('aria-hidden');
      else o.base.setAttribute('aria-hidden', 'true');
    }
  }, [shown, options]);

  // 자동 전환 — 꺼져 있다(HERO_TIME_AUTO_SWITCH=false). 켜면: idle 에 한 번, 손대지 않은 방문자에게만. 그림은 층이 다 받은 뒤에야 보인다.
  useEffect(() => {
    if (!HERO_TIME_AUTO_SWITCH || !box || options.length === 0) return;
    const want = heroShotForHour(new Date().getHours(), options.map((o) => o.shot));
    if (want === HERO_SHOT_DAY) return;
    const run = () => { if (!touched.current) choose(want, 'auto'); };
    const w = window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number; cancelIdleCallback?: (id: number) => void };
    if (typeof w.requestIdleCallback === 'function') {
      const id = w.requestIdleCallback(run, { timeout: 4000 });
      return () => { try { w.cancelIdleCallback?.(id); } catch { /* 무시 */ } };
    }
    const t = window.setTimeout(run, 2500);
    return () => window.clearTimeout(t);
  }, [box, options, choose]);

  if (!box || options.length === 0) return null;

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    const next = radioKeyIndex(e.key, index, options.length);
    if (next == null) return;
    e.preventDefault();
    choose(options[next].shot, 'key');
    radios.current[next]?.focus();
  };

  return createPortal(
    <>
      {options.filter((o) => o.shot !== HERO_SHOT_DAY && mounted.includes(o.shot)).map((o) => (
        <picture key={o.shot} className="kd-hts-layer" data-on={shown === o.shot ? '' : undefined} aria-hidden={shown === o.shot ? undefined : true}>
          {o.webp ? <source type="image/webp" srcSet={o.webp} /> : null}
          {/* eslint-disable-next-line @next/next/no-img-element -- 수화 뒤에만 서는 겹침 층(SSR HTML 에 없다). 주소는 리졸버가 준 자체 도메인 master JPEG + 표시용 webp. */}
          <img
            src={o.jpg}
            alt={o.alt}
            width={o.width}
            height={o.height}
            decoding="async"
            draggable={false}
            data-kd-shot={o.shot}
            onLoad={() => markLoaded(o.shot)}
            ref={(el) => { if (el && el.complete && el.naturalWidth > 0) markLoaded(o.shot); }}
          />
        </picture>
      ))}
      <div className="kd-hts" role="radiogroup" aria-label="상상도 시간대 고르기" data-vx-time="1">
        {options.map((o, i) => (
          <button
            key={o.shot}
            ref={(el) => { radios.current[i] = el; }}
            type="button"
            role="radio"
            className="kd-hts__b"
            aria-checked={active === o.shot}
            tabIndex={active === o.shot ? 0 : -1}
            onClick={() => choose(o.shot, 'toggle')}
            onKeyDown={(e) => onKeyDown(e, i)}
          >
            <span data-rd="label">{o.label}</span>
          </button>
        ))}
      </div>
    </>,
    box,
  );
}
